import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { getTerminals } from "../API/getTerminals";
import { saveTerminalMasterState } from "../AppState/terminalMaster";
import { TerminalSelection } from "../AppState/terminalSelection";
import { TTerminal } from "../AppState/overagesFilterFormActions";
import { sortTerminals, userTerminal } from "../helpers";
import AppState from "../AppState";

/**
 * Custom hook for managing terminal master data
 * 
 * This hook encapsulates the common terminal loading logic used across multiple components.
 * It fetches terminals from the API, sorts them, and automatically sets the user's default 
 * terminal if no terminal is currently selected.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { loadTerminals } = useTerminalMaster();
 *   const terminalOptions = useSelector(terminalValuesSelector);
 * 
 *   useEffect(() => {
 *     if (!terminalOptions?.length) {
 *       loadTerminals();
 *     }
 *   }, [loadTerminals, terminalOptions?.length]);
 * 
 *   // Component JSX...
 * }
 * ```
 * 
 * @returns {Object} Hook methods
 * @returns {Function} loadTerminals - Function to load and initialize terminal master data
 */
export const useTerminalMaster = () => {
  const dispatch = useDispatch();
  const activeTerminal = useSelector((state: AppState) => state.activeTerminal);

  /**
   * Fetches terminal master data and handles terminal selection logic
   * 
   * This function:
   * 1. Calls the getTerminals API
   * 2. Sorts the results using the sortTerminals helper
   * 3. Saves the sorted terminals to Redux state
   * 4. Auto-selects the user's terminal if no terminal is currently selected
   */
  const loadTerminals = useCallback(() => {
    getTerminals()
      .then((results: TTerminal[]) => {
        dispatch(saveTerminalMasterState(sortTerminals(results)));
        
        // Auto-select user's terminal if no terminal is currently selected
        if (activeTerminal?.terminalId === "") {
          dispatch(
            TerminalSelection(
              results?.find(
                (terminal: TTerminal) =>
                  terminal?.terminalId === userTerminal() &&
                  terminal?.region !== null
              ) ?? results[0]
            )
          );
        }
      })
      .catch((error: any) => {
        console.error(error);
      });
  }, [dispatch, activeTerminal?.terminalId]);

  return {
    loadTerminals
  };
};